import { useEffect, useState } from 'react'

import { Button, Progress } from 'antd'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { getFamilyUserData, getUserRole, userSelector } from 'features/user/model/selectors/userSelectors'
import styled from "styled-components"
import { currentPackageSelector, defaultPackageSelector, familyMembersSelector, familySelector } from 'features/team/model/selectors/teamSelectors'
import { CheckCircleFilled, PlusOutlined } from 'shared/ui'
import { setIsInviteMembersModalOpen, setIsPlansModalOpen } from 'features/common/model/slice/appSlice'
import { ConfirmationModal } from 'features/common/ui/ConfirmationModal'
import { ClockCircleFilled, LikeFilled, RetweetOutlined, RightOutlined } from '@ant-design/icons'
import { fetchCustomerPortalLinkThunk, fetchPackagesThunk } from 'features/team/model/slice/teamSlice'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import { checkPermission } from 'shared/utils/checkPermission'
import { MemberRole } from 'features/team/model/types/members'
import { useTranslation } from 'react-i18next'

const formatStorageSize = (bytes: number): string => {
  const gb = bytes / (1024 * 1024 * 1024)
  return `${Math.round(gb)} GB`
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export const PlanInfo = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const currentPackage = useAppSelector(currentPackageSelector)
  const defaultPackage = useAppSelector(defaultPackageSelector)
  const userRole = useAppSelector(getUserRole)
  const familyUserData = useAppSelector(getFamilyUserData)
  const familyData = useAppSelector(familySelector)

  const activePackage = currentPackage || defaultPackage

  const { storage_size } = useAppSelector(userSelector)
  const quotaSize = familyUserData?.quota_size
  const familyMembers = useAppSelector(familyMembersSelector)
  
  const planName = currentPackage?.name || 'Starter'
  const planCost = activePackage ? activePackage.cost : 0
  const planPeriod = activePackage?.period_type || 'month'
  const planStorage = activePackage?.size
  const MAX_MEMBERS = familyData?.members_limit
  
  const currentMembers = familyMembers.length
  const storagePercentage = Math.max(1, Math.round((storage_size / planStorage) * 100))

  const [maxMembersConfirmationModalOpen, setMaxMembersConfirmationModalOpen] = useState(false)
  const [isCustomerPortalLoading, setIsCustomerPortalLoading] = useState(false)

  useEffect(() => {
    if (searchParams.get('successfull_payment') === '1') {
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'The plan has been changed',
        message: '',
      })

      navigate('/profile/plan', { replace: true })
    }

    if (searchParams.get('failed_payment') === '1') {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'We couldn’t update your plan. Check your payment and try again',
        message: '',
      })

      navigate('/profile/plan', { replace: true })
    }
  }, [searchParams])

  const handleInviteMembers = () => {
    if (currentMembers >= MAX_MEMBERS) {
      setMaxMembersConfirmationModalOpen(true)

      return
    }

    dispatch(setIsInviteMembersModalOpen(true))
  }

  const handleOpenPlansModal = () => {
    setMaxMembersConfirmationModalOpen(false)
    dispatch(setIsPlansModalOpen(true))
  }

  const handleOpenCustomerPortal = () => {
    setIsCustomerPortalLoading(true)

    dispatch(fetchCustomerPortalLinkThunk({ callback: (url) => {
      window.location.href = url
      setIsCustomerPortalLoading(false)
    }, errorCallback: () => {
      console.error('Error fetching customer portal link')
      setIsCustomerPortalLoading(false)
    } }))
  }

  const handleUpdate = () => {
    dispatch(fetchPackagesThunk())
  }

  const planStorageSize = formatStorageSize(quotaSize).split(' ')[0]
  const planStorageUnit = formatStorageSize(quotaSize).split(' ')[1]
  const storageSize = formatBytes(storage_size).split(' ')[0]
  const storageUnit = formatBytes(storage_size).split(' ')[1]

  if (!currentPackage && !defaultPackage) {
    return (
      <SPlaceholderBox>
        <SClockIcon />

        <SPlaceholderTitle>
          Oops! Couldn’t load data
        </SPlaceholderTitle>

        <SPlaceholderDescription>
          Updating your information, please wait
        </SPlaceholderDescription>

        <SPlaceholderButton onClick={handleUpdate}>
          Update
        </SPlaceholderButton>
      </SPlaceholderBox>
    )
  }

  return (
    <div>
      {checkPermission(userRole, [MemberRole.OWNER], true, false) && (
        <SSectionWrapper>
          <SSection>
            <SPlanName>
              {planName}

              <SCheckIcon />
            </SPlanName>

            <SCurrentPlanInfo>
              <SCurrentPlanPrice>
                {planCost}

                <span>
                  {t(planPeriod === 'month' ? 'l_plans_priceMonth' : 'l_plans_priceYear')}
                </span>
              </SCurrentPlanPrice>
            </SCurrentPlanInfo>

            {/* <SCurrentPlanDescription>
            {!currentPackage && 'You are on a free plan.'}
          </SCurrentPlanDescription> */}

            {!currentPackage && checkPermission(userRole, [MemberRole.OWNER], true, false) && (
              <SUpgradePlanButton
                type="primary"
                size='large'
                onClick={handleOpenPlansModal}
              >
                {t('a_plans_upgradeYourPlan')}
              </SUpgradePlanButton>
            )}

            {
              !!currentPackage && checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER], true, false) && (
                <SUpgradePlanButton
                  size='large'
                  onClick={handleOpenCustomerPortal}
                  loading={isCustomerPortalLoading}
                >
                  <RetweetOutlined />

                  {t('a_plans_manage')}
                </SUpgradePlanButton>
              )}
          </SSection>

          {currentPackage?.period_type === 'month' && (
            <SUpdatePlan onClick={handleOpenCustomerPortal}>
              <SLikeIcon />

              {t('l_notification_switchAnsSave')}

              <SArrowIcon />
            </SUpdatePlan>
          )}
        </SSectionWrapper>
      )}

      <SIncludedTitle>
        {t('l_plans_planDetails')}
      </SIncludedTitle>

      <SSection>
        <SStorageSpaceTitle>
          {t('l_plans_storageInfo', { size: planStorageSize, unit: planStorageUnit })}
        </SStorageSpaceTitle>

        <Progress
          percent={storagePercentage}
          showInfo={false}
          strokeColor="var(--color-primary)"
        />

        <SStorageSpaceUsage>
          {t('l_plans_storageUsed', { 
            UsedSize: storageSize, 
            UsedUnit: storageUnit, 
            size: planStorageSize, 
            unit: planStorageUnit 
          })}
        </SStorageSpaceUsage>
      </SSection>

      <SSection className="mt-16">
        <SStorageSpaceTitle>
          {t('l_plans_membersUsed', { membersUsed: currentMembers, members: MAX_MEMBERS })}
        </SStorageSpaceTitle>

        <SMembersRow>
          {familyMembers.slice(0, 7).map((member) => (
            <SAvatar
              key={member.id}
            >
              {member.name?.[0] || '?'}
            </SAvatar>
          ))}

          {familyMembers.length > 7 && (
            <SAvatar className="dark">
              +
              {familyMembers.length - 7}
            </SAvatar>
          )}

          <SAddButton
            size='large'
            onClick={handleInviteMembers}
          >
            <PlusOutlined />
          </SAddButton>
        </SMembersRow>
      </SSection>

      <ConfirmationModal
        open={!!maxMembersConfirmationModalOpen}
        title={t('l_plans_maxMembersTitle')}
        description={t('l_plans_maxMembersMsg')}
        onCancel={() => setMaxMembersConfirmationModalOpen(false)}
        onConfirm={handleOpenPlansModal}
        confirmText={t('a_plans_upgradeYourPlan')}
        cancelText={t('a_common_cancel')}
      />
    </div>
  )
}

const SSection = styled.div`
  margin-top: 20px;
  background-color: #F5F5F5;
  padding: 32px;
  border-radius: 16px;
  position: relative;
  z-index: 2;

  &.mt-16 {
    margin-top: 16px;
  }
`

const SPlanName = styled.div`
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: #000000E0;
  display: flex;
  align-items: center;
  gap: 6px;
`

const SCheckIcon = styled(CheckCircleFilled)`
  font-size: 20px;
  color: #52C41A;
`

const SCurrentPlanInfo = styled.div`
  margin-top: 24px;
`

const SCurrentPlanPrice = styled.p`
  font-size: 38px;
  line-height: 46px;
  font-weight: 600;
  color: #000000E0;

  span {
    font-size: 16px;
    line-height: 24px;
  }
`

const SUpgradePlanButton = styled(Button)`
  margin-top: 32px;
  width: 362px;
`

const SIncludedTitle = styled.p`
  margin-top: 40px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: #000000E0;
`

const SStorageSpaceTitle = styled.p`
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
  color: #000000E0;
`

const SStorageSpaceUsage = styled.p`
  margin-top: 12px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #00000073;
`

const SMembersRow = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`

const SAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 400;
  color: #FFFFFF;
  background-color: #B8B8B8;

  &:not(:first-of-type) {
    margin-left: -4px;
  }

  &.dark {
    background-color: #8C8C8C;
  }
`

const SAddButton = styled(Button)`
  margin-left: 12px;
  width: 40px;
`

const SSectionWrapper = styled.div`
  width: 100%;
  position: relative;
`

const SUpdatePlan = styled.div`
  margin-top: -21px;
  height: 78px;
  background-color: #E6CCFF;
  display: flex;
  align-items: center;
  padding: 21px 24px 0;
  font-size: 14px;
  font-weight: 400;
  color: #000000E0;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  cursor: pointer;
  box-sizing: border-box;
`

const SLikeIcon = styled(LikeFilled)`
  margin-right: 8px;
  font-size: 20px;
  color: #8747DF;
`

const SArrowIcon = styled(RightOutlined)`
  margin-left: auto;
  font-size: 18px;
  color: #000000E0;
`

const SPlaceholderBox = styled.div`
  width: 100%;
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #F5F5F5;
  border-radius: 16px;
  box-sizing: border-box;
`

const SClockIcon = styled(ClockCircleFilled)`
  font-size: 24px;
  color: #00000073;
`

const SPlaceholderTitle = styled.p`
  margin-top: 16px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
  color: #00000073;
`

const SPlaceholderDescription = styled.p`
  margin-top: 4px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #00000073;
`

const SPlaceholderButton = styled(Button)`
  margin-top: 16px;
`